import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import SectionLink from "./SectionLink";

const NAV_EASE = [0.22, 1, 0.36, 1];
const MENU_ICON_SIZE = 18;

const HEADER_HIDDEN = { y: -60, opacity: 0 };
const HEADER_VISIBLE = { y: 0, opacity: 1 };
const HEADER_TRANSITION = { duration: 0.9, ease: NAV_EASE };
const MOBILE_MENU_HIDDEN = { height: 0, opacity: 0 };
const MOBILE_MENU_VISIBLE = { height: "auto", opacity: 1 };
const MOBILE_MENU_TRANSITION = { duration: 0.35, ease: NAV_EASE };

export const Logo = ({ light = false }) => (
  <Link to="/" data-testid="site-logo" aria-label="The Formulation House — home" className="flex items-center gap-3 select-none">
    <img
      src="/logo.png"
      alt=""
      width="48"
      height="48"
      className={`w-12 h-12 rounded-full shrink-0 ${light ? "ring-1 ring-ivory/30" : ""}`}
    />
    <span className="leading-none">
      <span className={`block font-display text-[17px] tracking-tight ${light ? "text-ivory" : "text-navy"}`}>
        The Formulation House
      </span>
      <span className={`block text-[9px] uppercase tracking-[0.35em] mt-1 ${light ? "text-ivory/60" : "text-navy/50"}`}>
        Formulation Studio
      </span>
    </span>
  </Link>
);

export const NAV_LINKS = [
  { label: "Why Us", id: "why-us" },
  { label: "Expertise", id: "expertise" },
  { label: "Services", id: "services" },
  { label: "Process", id: "process" },
  { label: "Categories", id: "categories" },
];

const DesktopMenu = () => (
  <nav aria-label="Primary" className="hidden lg:flex items-center gap-10">
    {NAV_LINKS.map(({ label, id }) => (
      <SectionLink
        key={id}
        id={id}
        data-testid={`nav-link-${id}`}
        className="group relative text-[13px] font-medium tracking-wide text-charcoal/80 hover:text-navy transition-colors duration-300"
      >
        {label}
        <span className="absolute -bottom-1.5 left-0 h-px w-full bg-navy origin-left scale-x-0 transition-transform duration-300 ease-out group-hover:scale-x-100" />
      </SectionLink>
    ))}
  </nav>
);

const MobileMenu = ({ open, onNavigate }) => (
  <AnimatePresence>
    {open && (
      <motion.nav
        data-testid="nav-mobile-menu"
        aria-label="Mobile"
        initial={MOBILE_MENU_HIDDEN}
        animate={MOBILE_MENU_VISIBLE}
        exit={MOBILE_MENU_HIDDEN}
        transition={MOBILE_MENU_TRANSITION}
        className="lg:hidden overflow-hidden bg-ivory/95 backdrop-blur-xl border-t border-navy/10"
      >
        <div className="px-6 py-6 flex flex-col gap-1">
          {NAV_LINKS.map(({ label, id }) => (
            <SectionLink
              key={id}
              id={id}
              onNavigate={onNavigate}
              data-testid={`nav-mobile-link-${id}`}
              className="text-left font-display text-2xl text-navy py-3 border-b border-navy/10 last:border-0"
            >
              {label}
            </SectionLink>
          ))}
        </div>
      </motion.nav>
    )}
  </AnimatePresence>
);

export default function Nav({ onContact }) {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <motion.header
      initial={HEADER_HIDDEN}
      animate={HEADER_VISIBLE}
      transition={HEADER_TRANSITION}
      className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-ivory/70 border-b border-navy/5"
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[76px] flex items-center justify-between">
        <Logo />
        <DesktopMenu />
        <div className="flex items-center gap-3">
          <button
            data-testid="nav-book-call-btn"
            onClick={onContact}
            className="group relative btn-shine overflow-hidden rounded-full bg-navy text-ivory text-[13px] font-semibold tracking-wide px-6 py-3 transition-transform duration-300 hover:-translate-y-0.5"
          >
            <span className="relative z-10">Book a Free Call</span>
          </button>
          <button
            data-testid="nav-mobile-menu-btn"
            onClick={() => setMenuOpen((o) => !o)}
            className="lg:hidden grid place-items-center w-11 h-11 rounded-full border border-navy/20 text-navy"
            aria-label="Toggle menu"
          >
            {menuOpen ? <X size={MENU_ICON_SIZE} strokeWidth={1.5} /> : <Menu size={MENU_ICON_SIZE} strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      <MobileMenu open={menuOpen} onNavigate={() => setMenuOpen(false)} />
    </motion.header>
  );
}
